<template>
  <v-container fluid class="pa-4">
    <!-- Page Header -->
    <v-row class="mb-4" align="center">
      <v-col>
        <div class="d-flex align-center">
          <v-icon large color="primary" class="mr-3">mdi-chart-box</v-icon>
          <div>
            <h1 class="text-h4 font-weight-bold">Service Reports</h1>
            <p class="subtitle-1 grey--text mb-0">
              Comprehensive service and repair analytics
            </p>
          </div>
        </div>
      </v-col>
      <v-col cols="auto">
        <v-btn text @click="$router.push('/services')">
          <v-icon left>mdi-arrow-left</v-icon>Back to Services
        </v-btn>
      </v-col>
    </v-row>

    <!-- Shared Filters -->
    <v-card class="mb-4" flat outlined>
      <v-card-text class="pb-2">
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              ref="fromMenu"
              v-model="fromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.from_date"
                  label="From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  clearable
                />
              </template>
              <v-date-picker
                v-model="filters.from_date"
                no-title
                @input="fromMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              ref="toMenu"
              v-model="toMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.to_date"
                  label="To Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  clearable
                />
              </template>
              <v-date-picker
                v-model="filters.to_date"
                no-title
                @input="toMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2" v-if="can('reports.branch_filter')">
            <v-select
              v-model="filters.branch_id"
              :items="branchOptions"
              item-text="branch_name"
              item-value="id"
              label="Branch"
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              @click="generateCurrentReport"
              :loading="loading"
            >
              <v-icon left>mdi-magnify</v-icon>Generate
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              outlined
              color="primary"
              @click="printCurrentReport"
              :disabled="!hasReportData"
            >
              <v-icon left>mdi-printer</v-icon>Print
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Report Tabs -->
    <v-card flat>
      <v-tabs v-model="activeTab" background-color="primary" dark show-arrows>
        <v-tab v-for="tab in tabs" :key="tab.key">
          <v-icon left small>{{ tab.icon }}</v-icon>
          <span class="text-none">{{ tab.label }}</span>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Revenue Summary -->
        <v-tab-item>
          <ServiceRevenueSummary
            ref="revenueSummaryReport"
            :filters="filters"
          />
        </v-tab-item>

        <!-- Technician Performance -->
        <v-tab-item>
          <TechnicianPerformance ref="technicianReport" :filters="filters" />
        </v-tab-item>

        <!-- Turnaround Time -->
        <v-tab-item>
          <ServiceTurnaroundTime ref="turnaroundReport" :filters="filters" />
        </v-tab-item>

        <!-- Monthly Trends -->
        <v-tab-item>
          <MonthlyServiceTrends ref="monthlyTrendsReport" :filters="filters" />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { printServiceReport } from "@/utils/serviceReportPrint";
import ServiceRevenueSummary from "@/components/ServiceReports/ServiceRevenueSummary.vue";
import TechnicianPerformance from "@/components/ServiceReports/TechnicianPerformance.vue";
import ServiceTurnaroundTime from "@/components/ServiceReports/ServiceTurnaroundTime.vue";
import MonthlyServiceTrends from "@/components/ServiceReports/MonthlyServiceTrends.vue";

export default {
  name: "ServiceReports",
  components: {
    ServiceRevenueSummary,
    TechnicianPerformance,
    ServiceTurnaroundTime,
    MonthlyServiceTrends,
  },
  data() {
    return {
      activeTab: 0,
      fromMenu: false,
      toMenu: false,
      filters: {
        from_date: this.defaultFromDate(),
        to_date: this.defaultToDate(),
        branch_id: null,
      },
      tabHasData: {},
      tabs: [
        {
          key: "revenue_summary",
          label: "Revenue Summary",
          icon: "mdi-currency-php",
        },
        {
          key: "technician_performance",
          label: "Technician Performance",
          icon: "mdi-account-hard-hat",
        },
        {
          key: "turnaround_time",
          label: "Turnaround Time",
          icon: "mdi-timer-sand",
        },
        {
          key: "monthly_trends",
          label: "Monthly Trends",
          icon: "mdi-chart-line",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      branches: "branch/branches",
      loading: "service_reports/loading",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
    branchOptions() {
      return this.branches || [];
    },
    currentRef() {
      const refs = [
        "revenueSummaryReport",
        "technicianReport",
        "turnaroundReport",
        "monthlyTrendsReport",
      ];
      return refs[this.activeTab];
    },
    hasReportData() {
      return !!this.tabHasData[this.activeTab];
    },
  },
  watch: {
    activeTab() {
      this.$nextTick(() => this.checkCurrentTabData());
    },
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    defaultFromDate() {
      let d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().substring(0, 10);
    },
    defaultToDate() {
      return new Date().toISOString().substring(0, 10);
    },
    checkCurrentTabData() {
      const comp = this.$refs[this.currentRef];
      this.$set(this.tabHasData, this.activeTab, !!(comp && comp.report));
    },
    async generateCurrentReport() {
      const comp = this.$refs[this.currentRef];
      if (comp && typeof comp.generate === "function") {
        await comp.generate();
        this.checkCurrentTabData();
      }
    },
    printCurrentReport() {
      const comp = this.$refs[this.currentRef];
      if (!comp || !comp.report) return;
      const tabKey = this.tabs[this.activeTab].key;
      printServiceReport(tabKey, comp.report, this.filters);
    },
  },
  mounted() {
    if (!this.branches || this.branches.length === 0) {
      this.$store.dispatch("branch/get_branches");
    }
  },
};
</script>
